export type SharedPublicRouteKey =
  | 'about'
  | 'privacy'
  | 'gdpr'
  | 'terms'
  | 'communityGuidelines'
  | 'contactSupport'
  | 'settings'
  | 'documents'
  | 'communityFeed';

export type SharedRichTextPart =
  | { type: 'text'; text: string }
  | { type: 'route'; key: SharedPublicRouteKey; label: string }
  | { type: 'email'; email: string; label?: string }
  | { type: 'external'; href: string; label: string };

export type SharedRichParagraph = SharedRichTextPart[];

export type SharedLegalSection = {
  title: string;
  paragraphs: SharedRichParagraph[];
};

export type SharedTextSection = {
  title: string;
  body: string;
};

export type SharedFeatureItem = {
  emoji: string;
  title: string;
  body: string;
};

export type SharedHowItWorksItem = {
  step: string;
  title: string;
  body: string;
};

export type SharedFaqItem = {
  question: string;
  answer: string;
};

export type SharedPublicLink = {
  key: Extract<SharedPublicRouteKey, 'about' | 'privacy' | 'gdpr' | 'terms' | 'communityGuidelines' | 'contactSupport'>;
  label: string;
};

const text = (value: string): SharedRichTextPart => ({ type: 'text', text: value });
const route = (key: SharedPublicRouteKey, label: string): SharedRichTextPart => ({ type: 'route', key, label });
const email = (emailAddress: string, label = emailAddress): SharedRichTextPart => ({
  type: 'email',
  email: emailAddress,
  label,
});
const external = (href: string, label: string): SharedRichTextPart => ({ type: 'external', href, label });
const paragraph = (...parts: SharedRichTextPart[]): SharedRichParagraph => parts;

export const PUBLIC_BETA_URLS = {
  testflightDownload: 'https://apps.apple.com/app/testflight/id899247664',
  iphoneBeta: 'https://testflight.apple.com/join/MCmmuXrF',
  androidBeta: 'https://play.google.com/apps/internaltest/4700882310345859386',
  steadyHouseWebsite: 'https://steadyhouse.co.uk',
  kindredWebsite: 'https://kindredcommunity.app',
  ico: 'https://ico.org.uk',
  icoRegistrationReference: 'ZC121264',
  supportEmail: 'hello@adultingapp.uk',
  legalEntityName: 'Steady House Ltd',
  founderName: 'Hannah Graham',
  companyNumber: '17095183',
  registeredOffice: 'Grosvenor House, 3 Chapel Street, Congleton, Cheshire, England, CW12 4AB',
  feedbackChannelName: 'Adulting feedback',
} as const;

export const PUBLIC_CORE_LINKS: SharedPublicLink[] = [
  { key: 'about', label: 'About Adulting' },
  { key: 'privacy', label: 'Privacy policy' },
  { key: 'gdpr', label: 'GDPR & data rights' },
  { key: 'terms', label: 'Terms & Conditions' },
  { key: 'communityGuidelines', label: 'Code of conduct' },
  { key: 'contactSupport', label: 'Contact & complaints' },
];

export const PUBLIC_CORE_LINK_LABELS = Object.fromEntries(
  PUBLIC_CORE_LINKS.map((link) => [link.key, link.label]),
) as Record<SharedPublicLink['key'], string>;

export const LANDING_PAGE_CONTENT = {
  title: 'One calm place for the bills, school admin, renewals, and appointments that keep landing on you',
  subtitle:
    'Adulting is the UK-first life admin app from Hannah Graham and Steady House Ltd. Money, home, health, family, work, calendar, and documents stay together so the invisible load feels lighter.',
  installTitle: 'Get Adulting on your phone',
  installSubtitle: 'Free during beta. No card required. Start with the next real task.',
  installBody:
    'Adulting beta testing is available on iPhone through TestFlight and on Android through Google Play internal testing.',
  chooseDeviceTitle: 'Choose your device',
  chooseDeviceSubtitle: 'Use the iPhone TestFlight path or the Android testing link below.',
  androidTitle: 'Android internal testing',
  androidSubtitle: 'Use the invited Google account first.',
  androidSteps: [
    'Sign into Google Play with the Google account that has been added to Adulting internal testing.',
    'Open the Adulting Android beta link and tap to become a tester.',
    'Install or update Adulting from the Play Store listing that appears after you join.',
  ],
  androidBody:
    'If Google Play says the testing programme is unavailable, switch to the invited Google account in the Play Store first, then open the Android beta link again.',
  forEveryoneTitle: 'Why Adulting exists',
  forEveryoneBody:
    'Adulting is built for the person carrying the invisible load: the renewals, the benefits, the school calendars, the NHS appointments, the forms, the deadlines, and the remembering. It is founder-led by Hannah Graham, shaped around real UK admin, and meant to feel calmer than stitching five tools together.',
  howItWorksTitle: 'How it works',
  howItWorksSubtitle:
    'Adulting should help in the first five minutes, not become another system you have to manage.',
  howItWorks: [
    {
      step: '1. Add the next thing that could slip',
      title: 'Start with one real task, not a full life overhaul',
      body:
        'Log the next bill, school payment, renewal, appointment, or document so the most urgent admin stops living in your head.',
    },
    {
      step: '2. See what matters in one place',
      title: 'Bring the scattered bits back together',
      body:
        'Adulting keeps money, home, health, family, work, calendar, and documents in one calm system so you can see what is due next.',
    },
    {
      step: '3. Stay ahead before it turns into stress',
      title: 'Catch deadlines before they cost you',
      body:
        'Use reminders, renewals, and section-by-section admin tools to stay on top of real life without stitching five apps together.',
    },
  ] satisfies SharedHowItWorksItem[],
  everythingTitle: 'What lives inside Adulting',
  features: [
    { emoji: '💰', title: 'My Money', body: 'Budgets, bills, debt, and benefits in the same place as the rest of life admin.' },
    { emoji: '🏠', title: 'My Home', body: 'Tenancy details, renewals, utilities, and household admin that is easy to lose track of.' },
    { emoji: '🧠', title: 'My Health', body: 'Appointments, NHS reminders, wellbeing check-ins, and the details you keep re-finding.' },
    { emoji: '👨‍👩‍👧', title: 'My People', body: 'School dates, care admin, people plans, and the shared load that usually lives in your head.' },
    { emoji: '💼', title: 'My Work', body: 'Employment rights, HMRC deadlines, self-employment basics, and work admin in one place.' },
  ] satisfies SharedFeatureItem[],
  benefitsTitle: 'Why it feels calmer',
  benefitsSubtitle: 'The goal is fewer dropped balls and less mental load, not more features for the sake of it.',
  benefits: [
    {
      title: 'Keep money admin and real life in the same loop',
      body:
        'Track budgets, bills, debt, and benefits in the same place as everything else, so money admin stops becoming a separate stressful pile.',
    },
    {
      title: 'See school, home, health, and care dates before they get missed',
      body:
        'Bring renewals, NHS reminders, family events, and household tasks into one view so fewer important dates disappear into messages and paper.',
    },
    {
      title: 'Store the details you keep re-finding',
      body:
        'Keep documents, dates, and key context close to the task itself, so you are not reopening the same email thread or searching your camera roll again.',
    },
    {
      title: 'Use a UK-first setup instead of building your own system',
      body:
        'Adulting is shaped around the admin load people actually carry in the UK, from benefits and HMRC to school, tenancy, and NHS reminders.',
    },
  ] satisfies SharedTextSection[],
  trustTitle: 'Why people trust this approach',
  trust: [
    { emoji: '🇬🇧', title: 'UK-first', body: 'Every benefit, every law, every NHS link built for the UK.' },
    { emoji: '💜', title: 'Built by a single parent', body: 'The hard moments, not just the milestones.' },
    { emoji: '🔒', title: 'Your data is yours', body: 'No ads. No selling your data. Ever.' },
    { emoji: '✨', title: 'Free during beta', body: 'No credit card needed while we learn what is most useful.' },
  ] satisfies SharedFeatureItem[],
  socialHelper:
    'Follow Adulting for practical updates and launch news from Hannah and Steady House, without turning the product itself into a feed.',
  nextTitle: 'From Hannah at Steady House',
  nextSubtitle: 'Adulting is a founder-led product. Explore the sister community app or the wider Steady House product studio if you want to see what else is being built.',
} as const;

export const PRICING_PAGE_CONTENT = {
  title: 'Pricing',
  subtitle: 'Adulting is free during beta while the product is still being shaped through real-world use.',
  accessTitle: 'Free beta access',
  accessSubtitle: 'No card required. No feature tiers. Everything currently available is included.',
  accessKicker: 'Beta access',
  accessPrice: 'Free',
  accessBody:
    'Adulting is built to reduce the admin load, not add another paywall. Everything currently available is free while beta testing is underway.',
  accessFooter: 'Start with one useful task and help shape what comes next.',
  includedTitle: "What's included",
  includedSubtitle: 'Everything below is included during beta.',
  included: [
    {
      title: 'One place for the admin load you are already carrying',
      body:
        'Keep money, home, health, family, work, calendar, and documents together so you spend less time re-finding what matters next.',
    },
    {
      title: 'Fewer missed renewals, deadlines, and appointments',
      body:
        'Track the dates and tasks most likely to slip before they turn into late fees, rushed calls, or another stressful catch-up.',
    },
    {
      title: 'Less setup than a blank task app',
      body:
        'Adulting is already shaped around real UK life admin, so you can start with the next practical task instead of building a system from scratch.',
    },
    {
      title: 'A calmer way to handle money, school, care, and household admin',
      body:
        'Use one system across the parts of life that usually sprawl across notes, messages, paper, and memory.',
    },
  ] satisfies SharedTextSection[],
  faqTitle: 'FAQ',
  faqSubtitle: 'Short answers to the questions people ask before they start.',
  faqs: [
    {
      question: 'What is included during beta?',
      answer:
        'Everything currently available in Adulting is included during beta. There are no feature tiers or locked sections right now.',
    },
    {
      question: 'Is it really free?',
      answer: 'Yes. Adulting is free during beta and you do not need a card to get started.',
    },
    {
      question: 'Can I cancel anytime?',
      answer:
        'Yes. There is no contract. If you stop using Adulting during beta, there is nothing to cancel or be charged for.',
    },
    {
      question: 'Who is Adulting for?',
      answer:
        'Adulting is for the person carrying most of the admin load, especially if money, school, home, health, and work tasks keep landing on them.',
    },
    {
      question: 'Do I need to set everything up at once?',
      answer:
        'No. Start with the next bill, renewal, appointment, or document that is easiest to forget and build from there.',
    },
    {
      question: 'Will there be pricing later?',
      answer:
        'Possibly. Adulting is free while beta testing is underway, and any later pricing would be introduced once the product is clearly useful and more mature.',
    },
  ] satisfies SharedFaqItem[],
  joinTitle: 'Join Adulting free during beta',
  joinBody:
    'Start with one bill, one renewal, one appointment, or one document. Adulting is designed to help from the first useful task, not after a big setup project.',
} as const;

export const ABOUT_PAGE_CONTENT = {
  title: 'About Adulting',
  subtitle: 'What Adulting is, what it does, and what this beta is for.',
  introTitle: 'About Adulting',
  introSubtitle: 'Adulting LifeOS · founder-led by Hannah Graham · Steady House Ltd · April 2026',
  introBody:
    'Adulting is a life admin assistant for the stuff people often carry silently: finances, forms, deadlines, housing admin, health reminders, family logistics, work tasks, and the mental load of holding them together. Adulting is operated by Steady House Ltd (company 17095183), registered in England and Wales, so the legal and operational responsibility sits with the company. It is still a founder-led product built by Hannah Graham and meant to keep improving around what members actually ask for and need.',
  pillars: [
    {
      title: 'One place for real-life admin',
      body:
        'Adulting brings money, home, health, family, work, documents, and calendar into one calmer system so you are not holding everything in your head.',
    },
    {
      title: 'Practical, not performative',
      body:
        'The app is designed around reminders, scripts, checklists, and next steps that make awkward or heavy admin feel lighter.',
    },
    {
      title: 'Your data is not the product',
      body:
        'We do not sell your data. We prioritise keeping it safe, limiting access, and giving you account controls like password reset, export, deletion, and authenticator-app security.',
    },
  ] satisfies SharedTextSection[],
  betaTitle: 'What beta means right now',
  betaBody: [
    'Adulting is stable enough for daily use, but some areas are still growing in depth and later integrations are deliberately phased rather than rushed.',
    'Adulting now stays aligned across web, iPhone, and Android so the same core routes and product structure are available whichever surface you use.',
    'The UI stays intentionally standard across screens so the product feels calm, consistent, and predictable instead of becoming a pile of one-off tools.',
    'Signed-in members can share product bugs, ideas, and requests in the Adulting feedback channel in Community. That route exists on purpose so Adulting can keep improving around what people actually need, while privacy, data-rights, and complaint issues still use the formal contact route.',
  ],
  notTitle: 'What Adulting is not',
  notBody: [
    'Adulting is not a substitute for qualified legal, financial, tax, or medical advice.',
    'It is not built around selling your data or pushing you into private messages, upsells, or noisy growth loops.',
    'It is a practical planning product first: reminders, scripts, structure, and calmer decision support.',
  ],
  socialHelper:
    'If you want broader beta updates or a public link to pass on, the social accounts live here without becoming part of the main app flow.',
} as const;

export const PRIVACY_PAGE_CONTENT = {
  title: 'Privacy policy',
  subtitle: 'How Adulting collects, uses, shares, stores, and protects your personal data.',
  introTitle: 'Privacy policy',
  introBody: paragraph(
    text('Adulting is built to help you manage real-life admin without turning your data into the product. This policy explains what information we handle, why we handle it, and the controls you already have inside the app.'),
  ),
  sections: [
    {
      title: 'Who is responsible for your data',
      paragraphs: [
        paragraph(text('Steady House Ltd, operating Adulting, is the data controller responsible for deciding how your personal data is used in the product.')),
        paragraph(text('Steady House Ltd is registered in England and Wales under company number '), text(PUBLIC_BETA_URLS.companyNumber), text(', with registered office at '), text(PUBLIC_BETA_URLS.registeredOffice), text('.')),
        paragraph(text('Steady House Ltd is also registered with the UK Information Commissioner’s Office (ICO), registration reference '), text(PUBLIC_BETA_URLS.icoRegistrationReference), text('.')),
        paragraph(text('For privacy questions, data rights requests, or complaints, use '), route('contactSupport', 'Contact & complaints'), text(' or email '), email(PUBLIC_BETA_URLS.supportEmail), text('.')),
      ],
    },
    {
      title: 'What we collect',
      paragraphs: [
        paragraph(text('Your account details, sign-in method, and profile information.')),
        paragraph(text('The life-admin information you choose to add, including reminders, events, renewals, budgets, debts, home details, health reminders, work admin, family details, and Life Check responses.')),
        paragraph(text('Document metadata and any files you upload to Adulting, plus external file identifiers and folder references when you choose to keep new documents in your own connected provider.')),
        paragraph(text('Security, support, moderation, and product-usage records needed to keep the beta working safely and reliably.')),
        paragraph(text('If you choose an integration, the related provider data needed to complete that connection, such as Google or Apple sign-in, Google Drive imports or storage, Google Calendar sync details, Apple Calendar sync details on iPhone, or bank-connection data when that flow is active.')),
      ],
    },
    {
      title: 'Why we use your data and our lawful bases',
      paragraphs: [
        paragraph(text('Contract: to create your account, run the product features you ask for, store your documents, show your reminders, and let you export or delete your data.')),
        paragraph(text('Legitimate interests: to secure the service, prevent abuse, troubleshoot issues, monitor reliability during beta, improve the product, and moderate community spaces.')),
        paragraph(text('Legal obligation: where we need to keep or disclose information to comply with the law, respond to lawful requests, or handle serious safety matters.')),
        paragraph(text('Consent: where required for optional connections or any future non-essential cookies or direct marketing choices.')),
        paragraph(text('Special category data: where you choose to add optional health information, detailed dependant information, or similarly sensitive uploaded content, Adulting relies on your explicit consent so those optional features can work for you.')),
      ],
    },
    {
      title: 'Who we share data with',
      paragraphs: [
        paragraph(text('We use trusted processors for hosting, authentication, database operations, file storage, and operational email delivery where those services are needed to run Adulting.')),
        paragraph(text('If you choose to connect third-party services, those providers will process the information needed for that connection under their own terms and privacy rules as well as ours.')),
        paragraph(text('We do not sell, rent, or trade your personal data. We do not use your life-admin data for advertising.')),
      ],
    },
    {
      title: 'International transfers',
      paragraphs: [
        paragraph(text('Some service providers may process data outside the UK. Where that happens, Adulting relies on UK adequacy decisions or other appropriate safeguards such as contractual protections.')),
      ],
    },
    {
      title: 'Cookies, local storage, and device data',
      paragraphs: [
        paragraph(text('Adulting uses essential session and device storage to sign you in, protect your account, remember onboarding progress, and keep simple preferences such as your default section.')),
        paragraph(text('Adulting does not currently rely on non-essential advertising cookies. If we add non-essential analytics, marketing, or similar tracking technologies later, we will ask first where the law requires it.')),
      ],
    },
    {
      title: 'How long we keep data',
      paragraphs: [
        paragraph(text('Your account data stays in Adulting until you update it, export it, or delete it.')),
        paragraph(text('If you use the delete option in Settings, Adulting removes your stored app data and Adulting-hosted uploaded files from the live product, while files you chose to keep in your own connected providers remain in those provider accounts. Limited operational or security records may remain temporarily where genuinely needed for audit, abuse prevention, or incident handling.')),
        paragraph(text('We may keep moderation and legal records for longer where needed to protect users, respond to disputes, or comply with the law.')),
      ],
    },
    {
      title: 'How we protect data',
      paragraphs: [
        paragraph(text('Adulting uses authenticated access, role-based permissions, encrypted transport over HTTPS, and platform-level controls for database and storage access.')),
        paragraph(text('Admin and moderation access is limited and protected with stronger security controls, including multi-factor authentication.')),
      ],
    },
    {
      title: 'Your choices and rights',
      paragraphs: [
        paragraph(text('You can change your password, export your data, and delete your app data from '), route('settings', 'Settings'), text('.')),
        paragraph(text('You can manage uploaded files from '), route('documents', 'Documents'), text('.')),
        paragraph(text('If you no longer want Adulting to hold optional health, dependant, or sensitive uploaded information, remove that information from the app, delete the related files, or contact '), email(PUBLIC_BETA_URLS.supportEmail), text(' for help with erasure.')),
        paragraph(text('You can read the full GDPR rights summary on the '), route('gdpr', 'GDPR & data rights'), text(' page.')),
        paragraph(text('If your issue is a product idea or bug rather than a privacy complaint, use the '), text(PUBLIC_BETA_URLS.feedbackChannelName), text(' channel in '), route('communityFeed', 'Community'), text('.')),
        paragraph(text('If you are not satisfied, you can also complain to the ICO at '), external(PUBLIC_BETA_URLS.ico, 'ico.org.uk'), text('.')),
      ],
    },
  ] satisfies SharedLegalSection[],
} as const;

export const TERMS_PAGE_CONTENT = {
  title: 'Terms of use',
  subtitle: 'The core rules for using Adulting during beta.',
  introTitle: 'Terms of use',
  introBody: paragraph(
    text('By using Adulting, you agree to use the app lawfully, respectfully, and with the understanding that the product is still evolving during beta.'),
  ),
  sections: [
    {
      title: 'What Adulting is',
      paragraphs: [
        paragraph(text('Adulting is a planning and life-admin support tool operated by Steady House Ltd. It gives you reminders, prompts, structure, checklists, and product guidance.')),
        paragraph(text('Adulting is not legal, tax, financial, or medical advice. You remain responsible for checking anything important with the right organisation or qualified professional before you act on it.')),
      ],
    },
    {
      title: 'Your account and use of the service',
      paragraphs: [
        paragraph(text('Keep your login details secure and only use Adulting for lawful purposes.')),
        paragraph(text("Do not try to access other users' data, misuse the service, scrape the product, interfere with its operation, or bypass security controls.")),
        paragraph(text('If you are given admin or moderation access, you must follow the extra security steps Adulting requires, including multi-factor authentication where prompted.')),
      ],
    },
    {
      title: 'Your content and documents',
      paragraphs: [
        paragraph(text('You are responsible for the information and files you add to Adulting.')),
        paragraph(text('Only upload or store content you have the right to use and keep, and avoid adding unnecessary sensitive information about other people.')),
        paragraph(text('If you use Adulting to keep records for children, dependants, or other adults, make sure you have a genuine reason to manage that information.')),
      ],
    },
    {
      title: 'Community rules',
      paragraphs: [
        paragraph(text('If you use Adulting community features, you must keep them respectful, relevant, and safe.')),
        paragraph(text("No harassment, hate speech, threats, spam, scraping, impersonation, or sharing other people's personal data without consent.")),
        paragraph(text('Full conduct rules are on the '), route('communityGuidelines', 'Code of conduct'), text(' page.')),
      ],
    },
    {
      title: 'Third-party services and integrations',
      paragraphs: [
        paragraph(text('Some Adulting flows rely on third-party providers, such as sign-in providers, Google Drive document storage or imports, Google Calendar sync on web, Apple Calendar sync on iPhone, official information links, or later financial connections.')),
        paragraph(text('When you use those services, their own terms and privacy policies also apply.')),
      ],
    },
    {
      title: 'Availability, beta changes, and support',
      paragraphs: [
        paragraph(text('Adulting is in beta. Features may change, improve, move, or be removed as the product matures.')),
        paragraph(text('We aim for a dependable service, but we cannot promise uninterrupted availability, bug-free operation, or that every feature will remain exactly as it is today.')),
      ],
    },
    {
      title: 'Suspension and termination',
      paragraphs: [
        paragraph(text('Adulting may suspend or limit access if we reasonably believe an account is being misused, causing harm, breaching these terms, or creating security or legal risk.')),
        paragraph(text('You can stop using Adulting at any time. You can also delete your app data from Settings.')),
      ],
    },
    {
      title: 'Liability and important boundaries',
      paragraphs: [
        paragraph(text('Adulting is designed to reduce the mental load of admin, not to replace your own judgement or urgent professional help.')),
        paragraph(text('For critical deadlines, payments, legal issues, health issues, or safety concerns, verify the information yourself and use the relevant official or professional support.')),
      ],
    },
    {
      title: 'Contact',
      paragraphs: [
        paragraph(text('Questions about these terms, privacy, complaints, or data rights can be sent through '), route('contactSupport', 'Contact & complaints'), text(' or to '), email(PUBLIC_BETA_URLS.supportEmail), text('.')),
      ],
    },
  ] satisfies SharedLegalSection[],
} as const;

export const GDPR_PAGE_CONTENT = {
  title: 'GDPR & your data rights',
  subtitle: 'Your UK GDPR rights, how Adulting supports them, and how to get help.',
  introTitle: 'GDPR & your data rights',
  introBody: paragraph(
    text('UK GDPR gives you rights over your personal data. This page explains those rights in plain language and shows the quickest way to use them in Adulting.'),
  ),
  sections: [
    {
      title: 'Your rights',
      paragraphs: [
        paragraph(text('Be informed: you have the right to know what we collect, why we collect it, how long we keep it, and who it is shared with.')),
        paragraph(text('Access: you can ask for a copy of the personal data Adulting holds about you.')),
        paragraph(text('Rectification: you can ask us to correct inaccurate or incomplete personal data.')),
        paragraph(text('Erasure: you can ask us to delete your personal data where the law allows.')),
        paragraph(text('Restriction and objection: you can ask us to limit or stop certain processing in some situations.')),
        paragraph(text('Portability: where processing is based on contract or consent and carried out by automated means, you can ask for your data in a portable format.')),
        paragraph(text('Where Adulting relies on your explicit consent for optional health, dependant, or similarly sensitive uploaded information, you can withdraw that consent by deleting that information, deleting the related files, or asking us to erase it.')),
      ],
    },
    {
      title: 'What you can already do in the app',
      paragraphs: [
        paragraph(text('Export your Adulting data from '), route('settings', 'Settings'), text('.')),
        paragraph(text('Update profile details, work information, and dependant details from your account screens.')),
        paragraph(text('Delete document files from '), route('documents', 'Documents'), text('.')),
        paragraph(text('Delete your app data from Settings if you want to clear your account and start again.')),
      ],
    },
    {
      title: 'How to make a GDPR request',
      paragraphs: [
        paragraph(text('Use '), route('contactSupport', 'Contact & complaints'), text(' or email '), email(PUBLIC_BETA_URLS.supportEmail), text(' from the email address linked to your Adulting account, or explain clearly which account the request relates to.')),
        paragraph(text('We may need to verify your identity before we disclose, correct, or delete personal data.')),
        paragraph(text('We aim to respond without undue delay and normally within one calendar month. If a request is complex or you send several requests together, the law can allow more time, and we will tell you if that applies.')),
        paragraph(text('Most rights requests are free. If a request is manifestly unfounded, excessive, or repetitive, the law may allow a reasonable fee or a refusal, but we will explain that clearly first.')),
      ],
    },
    {
      title: 'Automated decisions and profiling',
      paragraphs: [
        paragraph(text('Adulting may show reminders, prompts, prioritisation, and suggested next steps based on the information in your account.')),
        paragraph(text('Adulting does not currently use solely automated decision-making that produces legal or similarly significant effects about you.')),
      ],
    },
    {
      title: 'Complaints',
      paragraphs: [
        paragraph(text('If you are unhappy with how Adulting has handled your information or a rights request, please contact us first through '), route('contactSupport', 'Contact & complaints'), text(' or at '), email(PUBLIC_BETA_URLS.supportEmail), text(' so we can try to put it right.')),
        paragraph(text('If your issue is really product feedback or a feature request, use the '), text(PUBLIC_BETA_URLS.feedbackChannelName), text(' channel in '), route('communityFeed', 'Community'), text(' instead of the GDPR route.')),
        paragraph(text('You also have the right to complain to the Information Commissioner\'s Office (ICO) at '), external(PUBLIC_BETA_URLS.ico, 'ico.org.uk'), text('.')),
      ],
    },
  ] satisfies SharedLegalSection[],
} as const;

export const COMMUNITY_GUIDELINES_PAGE_CONTENT = {
  title: 'Code of conduct',
  subtitle: 'The rules for using Adulting community during beta.',
  introTitle: 'Code of conduct',
  introBody: paragraph(
    text('Adulting community exists to give people carrying the life-admin load a safe, respectful place to find channels, direct support, and calmer next steps.'),
  ),
  sections: [
    {
      title: 'Our commitment',
      paragraphs: [
        paragraph(text('Adulting community should feel kind, useful, and calm rather than loud, performative, or unsafe.')),
        paragraph(text('We expect member-to-member support to stay grounded in lived experience, practical scripts, and respectful conversation.')),
      ],
    },
    {
      title: 'Expected behaviour',
      paragraphs: [
        paragraph(text('Treat other members with respect, patience, and kindness.')),
        paragraph(text('Use inclusive, non-shaming language and assume there is a real person behind the admin load being described.')),
        paragraph(text('Share what helped you, what script you used, or what calmer next step made a difference.')),
        paragraph(text('Use reports when something feels unsafe instead of arguing publicly or trying to moderate the space yourself.')),
      ],
    },
    {
      title: 'Unacceptable behaviour',
      paragraphs: [
        paragraph(text('No harassment, bullying, intimidation, pile-ons, discrimination, hate speech, threats, or repeated unwelcome contact.')),
        paragraph(text('No spam, scraping, fundraising, selling, self-promotion, impersonation, or attempts to use the community as a marketing channel.')),
        paragraph(text("No posting other people's personal data, documents, addresses, account details, or identifiable information without consent.")),
      ],
    },
    {
      title: 'Privacy and safety',
      paragraphs: [
        paragraph(text('Keep personal details light and practical. Public community spaces are not the place for full addresses, account numbers, document uploads, or exact location detail.')),
        paragraph(text('Do not move support into direct contact unless both people genuinely want that and it still feels safe.')),
        paragraph(text('We do not sell your data, and Adulting keeps community moderation and service access limited to what is needed to run the product safely.')),
      ],
    },
    {
      title: 'Content boundaries',
      paragraphs: [
        paragraph(text('Adulting community is for peer support, not professional legal, medical, tax, or financial advice.')),
        paragraph(text('If something sounds urgent, risky, or crisis-level, signpost proper help instead of trying to handle it in public or private community messages.')),
        paragraph(text('Keep community content relevant to adult-life admin, support, channels, and calmer next steps.')),
      ],
    },
    {
      title: 'Consequences of violations',
      paragraphs: [
        paragraph(text('Adulting may remove content, hide replies, restrict community access, suspend accounts, or permanently remove members when safety or repeated breaches require it.')),
        paragraph(text('Moderation is there to protect the space, not to shame people publicly.')),
      ],
    },
    {
      title: 'Reporting violations',
      paragraphs: [
        paragraph(text('If you see something unsafe, use the in-app reporting flow. Urgent help should go to emergency or crisis services, not to community chat.')),
        paragraph(text('Return to '), route('communityFeed', 'Community'), text(' if you want to review a channel or use the reporting flow inside Adulting.')),
        paragraph(text('Product ideas and bugs belong in the '), text(PUBLIC_BETA_URLS.feedbackChannelName), text(' channel in Community, not in unrelated support threads.')),
      ],
    },
    {
      title: 'Changes to this code',
      paragraphs: [
        paragraph(text('Adulting may revise this code of conduct as the community grows. Significant changes will be reflected in the app and on this page.')),
      ],
    },
  ] satisfies SharedLegalSection[],
} as const;

export const CONTACT_SUPPORT_PAGE_CONTENT = {
  title: 'Contact & complaints',
  subtitle: 'How to contact Adulting about support, privacy, data rights, moderation, or a complaint.',
  introTitle: 'Contact & complaints',
  introBody: paragraph(
    text('Adulting is operated by '),
    text(PUBLIC_BETA_URLS.legalEntityName),
    text(' (company '),
    text(PUBLIC_BETA_URLS.companyNumber),
    text('), registered in England and Wales. Registered office: '),
    text(PUBLIC_BETA_URLS.registeredOffice),
    text('. Steady House Ltd carries the formal legal and operational responsibility for Adulting, while the product remains founder-led by '),
    text(PUBLIC_BETA_URLS.founderName),
    text(' so support can stay human, practical, and accountable.'),
  ),
  sections: [
    {
      title: 'Support and account help',
      paragraphs: [
        paragraph(text('For account help, access problems, privacy questions, data-rights requests, moderation follow-up, or formal complaints, email '), email(PUBLIC_BETA_URLS.supportEmail), text('.')),
        paragraph(text('Use the account email linked to Adulting if you can, and include any urgent dates or deadlines that matter.')),
      ],
    },
    {
      title: 'Product feedback and suggestions',
      paragraphs: [
        paragraph(text('Signed-in members should use the '), text(PUBLIC_BETA_URLS.feedbackChannelName), text(' channel in '), route('communityFeed', 'Community'), text(' for bugs, ideas, and requests for what Adulting should improve or build next.')),
        paragraph(text('That route exists so Adulting can keep improving around what members actually need. It is for product feedback, not for GDPR requests or formal complaints.')),
      ],
    },
    {
      title: 'Privacy and ICO complaints',
      paragraphs: [
        paragraph(text('If you are unhappy with how Adulting has handled your data, contact us first at '), email(PUBLIC_BETA_URLS.supportEmail), text(' so we can try to put it right.')),
        paragraph(text('Steady House Ltd’s ICO registration reference is '), text(PUBLIC_BETA_URLS.icoRegistrationReference), text('. You can also complain to the ICO at '), external(PUBLIC_BETA_URLS.ico, 'ico.org.uk'), text(' if you are not satisfied after raising the issue with Adulting.')),
      ],
    },
    {
      title: 'Safety and safeguarding concerns',
      paragraphs: [
        paragraph(text('If you need to report exploitation, coercion, stalking, doxxing, serious safeguarding concerns, or other urgent safety issues connected to Adulting, email '), email(PUBLIC_BETA_URLS.supportEmail), text(' promptly and use the in-app report flow where available.')),
        paragraph(text('If there is an immediate risk of harm or someone needs urgent help, contact emergency or crisis services rather than waiting for an app response.')),
      ],
    },
  ] satisfies SharedLegalSection[],
} as const;
